import { CSSProperties, ReactNode } from 'react';
import { LoginLayout } from '@wavvest/shared-auth-ui';
import { ThemeModeSelector } from '@wavvest/shared-layout';
import { theme, type ThemeConfig } from 'antd';
import portfolioChart from '@/assets/login-preview/portfolio-chart.svg';
import wavvestLogoIcon from '@/assets/login-preview/wavvest-logo-icon.svg';
import wavvestLogoIconInk from '@/assets/login-preview/wavvest-logo-icon-ink.svg';
import wavvestLogoText from '@/assets/login-preview/wavvest-logo-text.svg';
import wavvestLogoTextInk from '@/assets/login-preview/wavvest-logo-text-ink.svg';
import {
  getAdvisorAuthBrandActionColors,
  getAdvisorAuthBrandHeroAccentColor,
} from './authBrandColors';
import { type AuthColorScheme, useAuthTheme } from './useAuthColorScheme';
import './index.scss';

const IMAGE_SOURCE_COMPARISON_BASE = 'https://wavvest.local';
const SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;

const normalizeImageSrcForComparison = (src?: string): string => {
  const trimmed = src?.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const hasScheme = SCHEME_PATTERN.test(trimmed);
    const url = hasScheme ? new URL(trimmed) : new URL(trimmed, IMAGE_SOURCE_COMPARISON_BASE);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      url.search = '';
      url.hash = '';
      if (!hasScheme && url.origin === IMAGE_SOURCE_COMPARISON_BASE) {
        return url.pathname;
      }
      return url.toString();
    }
  } catch {
    return trimmed;
  }

  return trimmed;
};

const getDistinctHeroImageSrc = (
  heroImageSrc?: string,
  logoSources: Array<string | undefined> = [],
): string | undefined => {
  const normalizedHero = normalizeImageSrcForComparison(heroImageSrc);
  if (!normalizedHero) {
    return undefined;
  }

  const duplicatesLogo = logoSources.some(
    (logoSource) => normalizeImageSrcForComparison(logoSource) === normalizedHero,
  );

  return duplicatesLogo ? undefined : heroImageSrc;
};

// The marketing site's type system (wavvest.com): Hanken Grotesk for
// interface copy, Fraunces for display. Ant components read their font from
// the theme token rather than inheriting it, so it is set there too.
const AUTH_SANS_STACK =
  "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Primary action per scheme when the firm has no brand color. `accent` is the
// solid stand-in for places a gradient cannot go: focus rings, checkboxes and
// Ant's own colorPrimary.
const WAVVEST_ACTION: Record<AuthColorScheme, { accent: string; fill: string; ink: string }> = {
  // The marketing site's primary call to action: gold with dark type.
  dark: {
    accent: '#d4ae66',
    fill: 'linear-gradient(180deg, #e8c77e 0%, #c6a05a 100%)',
    ink: '#1a1206',
  },
  // Ink on white, matching the light auth surface.
  light: { accent: '#0b0d12', fill: '#0b0d12', ink: '#ffffff' },
};

const WAVVEST_HERO_ACCENT: Record<AuthColorScheme, string> = {
  dark: '#c6a05a',
  light: '#1b63b7',
};

const resolveActionColors = (scheme: AuthColorScheme, brandColor?: string) => {
  if (scheme === 'light' || !brandColor) {
    return WAVVEST_ACTION[scheme];
  }
  const { backgroundColor, foregroundColor } = getAdvisorAuthBrandActionColors(brandColor, scheme);
  return { accent: backgroundColor, fill: backgroundColor, ink: foregroundColor };
};

const resolveHeroAccentColor = (
  scheme: AuthColorScheme,
  brandColor: string | undefined,
  hasHeroImage: boolean,
) => {
  const heroSurfaceScheme = hasHeroImage ? 'dark' : scheme;
  if (heroSurfaceScheme === 'light') {
    return WAVVEST_HERO_ACCENT.light;
  }
  if (!brandColor) {
    return WAVVEST_HERO_ACCENT[heroSurfaceScheme];
  }
  return getAdvisorAuthBrandHeroAccentColor(brandColor, heroSurfaceScheme);
};

const advisorSignupThemeComponents: ThemeConfig['components'] = {
  Button: {
    controlHeight: 44,
    fontSize: 15,
    borderRadius: 10,
    fontWeight: 600,
    primaryShadow: 'none',
    defaultShadow: 'none',
  },
  Input: {
    controlHeight: 44,
    fontSize: 15,
    borderRadius: 10,
  },
  Form: {
    itemMarginBottom: 16,
    labelFontSize: 13,
    verticalLabelPadding: '0 0 6px',
  },
};

const SignupProgress = ({ activeStep = 1 }: { activeStep?: number }) => (
  <div className="advisor-signup-progress" aria-label={`Signup step ${activeStep} of 4`}>
    {[1, 2, 3, 4].map((step, index) => (
      <div className="advisor-signup-progress-item" key={step}>
        <span
          className={`advisor-signup-progress-step${step === activeStep ? ' is-active' : ''}${
            step < activeStep ? ' is-complete' : ''
          }`}
        >
          {step}
        </span>
        {index < 3 && <span className="advisor-signup-progress-line" />}
      </div>
    ))}
  </div>
);

const advisorAccounts = [
  { name: 'Managed Brokerage', amount: '$142,680,400', delta: '+0.6%' },
  { name: 'Retirement Accounts', amount: '$86,412,300', delta: '+0.3%' },
  { name: 'Trust & Estate', amount: '$33,307,300', delta: '+0.5%' },
];

/**
 * How much room a firm's tagline needs. `introduction` is free text from the
 * firm's branding settings, so it can be a five-word motto or a paragraph. A
 * paragraph set at headline size would push everything else off the panel,
 * so long copy steps down and takes the product preview's space.
 */
const heroTitleSize = (title: string): 'display' | 'medium' | 'long' => {
  const normalizedTitle = title.trim().replace(/\s+/g, ' ');
  const length = normalizedTitle.length;
  const wordCount = normalizedTitle ? normalizedTitle.split(' ').length : 0;

  if (length <= 48 && wordCount <= 7) {
    return 'display';
  }
  return length <= 96 && wordCount <= 14 ? 'medium' : 'long';
};

const AumPreview = ({ logoMarkSrc }: { logoMarkSrc: string }) => (
  <div className="advisor-aum-card" aria-hidden="true">
    <div className="advisor-aum-summary">
      <div className="advisor-aum-heading-row">
        <div>
          <div className="advisor-aum-label-row">
            <span className="advisor-aum-label">Total AUM</span>
            <span className="advisor-live-pill">
              <span />
              Live
            </span>
          </div>
          <p className="advisor-aum-value">$262,400,000</p>
          <div className="advisor-aum-delta-row">
            <span className="advisor-aum-delta">+0.44%</span>
            <span>$1,148,200 today</span>
          </div>
        </div>
        <div className="advisor-aum-tabs">
          <span className="is-active">1M</span>
          <span>3M</span>
          <span>1Y</span>
          <span>All</span>
        </div>
      </div>
      <img className="portfolio-chart" src={portfolioChart} alt="" />
    </div>
    <div className="advisor-account-list">
      {advisorAccounts.map((account) => (
        <div className="advisor-account-row" key={account.name}>
          <div className="advisor-account-name">
            <span className="advisor-account-icon">
              <img src={logoMarkSrc} alt="" aria-hidden="true" />
            </span>
            <span>{account.name}</span>
          </div>
          <div className="advisor-account-values">
            <span>{account.amount}</span>
            <strong>{account.delta}</strong>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AdvisorHeroPanel = ({
  title,
  imageSrc,
  logoMarkSrc,
}: {
  title?: string;
  imageSrc?: string;
  logoMarkSrc: string;
}) => {
  const firmTitle = title?.trim();

  return (
    <section
      className={`advisor-signup-hero advisor-signup-hero--${imageSrc ? 'brand-photo' : 'fallback-art'}`}
      data-title-size={firmTitle ? heroTitleSize(firmTitle) : 'display'}
      aria-label="Advisor portal preview"
    >
      {imageSrc && <img className="advisor-brand-photo" src={imageSrc} alt="" aria-hidden="true" />}
      <div className="advisor-hero-content">
        <p className="advisor-portal-kicker">Advisor Portal</p>
        <h2 className="advisor-hero-title">
          {firmTitle || (
            <>
              <span>Built for advisors</span> <em>who build wealth.</em>
            </>
          )}
        </h2>
        {!imageSrc && <AumPreview logoMarkSrc={logoMarkSrc} />}
      </div>
    </section>
  );
};

type AdvisorSignupLayoutProps = {
  activeStep?: number;
  children: ReactNode;
  logoSrc?: string;
  logoFallbackSrc?: string;
  logoAlt?: string;
  brandColor?: string;
  heroImageSrc?: string;
  heroTitle?: string;
  showProgress?: boolean;
};

export const AdvisorSignupLayout = ({
  activeStep = 1,
  children,
  logoSrc,
  logoFallbackSrc,
  logoAlt = 'Wavvest Weave',
  brandColor,
  heroImageSrc,
  heroTitle,
  showProgress = true,
}: AdvisorSignupLayoutProps) => {
  const { colorScheme, setThemePreference, themePreference } = useAuthTheme();
  const action = resolveActionColors(colorScheme, brandColor);
  const heroImageToDisplay = getDistinctHeroImageSrc(heroImageSrc, [logoSrc, logoFallbackSrc]);
  const heroAccent = resolveHeroAccentColor(colorScheme, brandColor, Boolean(heroImageToDisplay));
  const cssVariables = {
    '--auth-accent': action.accent,
    '--auth-action-fill': action.fill,
    '--auth-action-ink': action.ink,
    '--auth-hero-kicker': heroAccent,
  } as CSSProperties;
  const themeToken: ThemeConfig['token'] = {
    fontFamily: AUTH_SANS_STACK,
    colorPrimary: action.accent,
  };

  return (
    <LoginLayout
      className={`main advisor-signup-main advisor-signup-main--${colorScheme}`}
      algorithm={colorScheme === 'light' ? theme.defaultAlgorithm : theme.darkAlgorithm}
      themeComponents={advisorSignupThemeComponents}
      themeToken={themeToken}
    >
      <div className="advisor-signup-wrapper" style={cssVariables}>
        <section className="advisor-signup-form-panel">
          <ThemeModeSelector
            className="advisor-signup-theme-selector"
            onChange={setThemePreference}
            value={themePreference}
          />
          <div className="advisor-signup-top">
            {logoSrc ? (
              <div className="advisor-signup-logo advisor-signup-logo-image">
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  onError={(event) => {
                    if (logoFallbackSrc && event.currentTarget.src !== logoFallbackSrc) {
                      event.currentTarget.src = logoFallbackSrc;
                    }
                  }}
                />
              </div>
            ) : (
              <div className="advisor-signup-logo advisor-signup-logo-default" aria-label="Wavvest">
                <span className="advisor-signup-logo-icon" aria-hidden="true">
                  <img
                    src={colorScheme === 'light' ? wavvestLogoIconInk : wavvestLogoIcon}
                    alt=""
                  />
                </span>
                <img
                  className="advisor-signup-logo-wordmark"
                  src={colorScheme === 'light' ? wavvestLogoTextInk : wavvestLogoText}
                  alt=""
                  aria-hidden="true"
                />
              </div>
            )}
            {showProgress && <SignupProgress activeStep={activeStep} />}
          </div>
          <div className="advisor-signup-body">{children}</div>
          <div className="advisor-signup-disclaimer">
            <p>
              Advisory services provided by Wavvest Wealth LLC, an SEC-registered investment
              adviser.
            </p>
            <p>
              By using this website, you agree to our{' '}
              <a href="https://wavvest-public-documents.s3.us-west-2.amazonaws.com/Wavvest/Wavvest+Terms+and+Conditions.pdf">
                Terms of Use
              </a>{' '}
              and <a href="https://www.wavvest.com/privacypolicy">Privacy Policy</a>. This site is
              protected by reCAPTCHA.
            </p>
          </div>
        </section>
        <AdvisorHeroPanel
          title={heroTitle}
          imageSrc={heroImageToDisplay}
          logoMarkSrc={colorScheme === 'light' ? wavvestLogoIconInk : wavvestLogoIcon}
        />
      </div>
    </LoginLayout>
  );
};
